
import { defineComponent, ref, computed, watch } from "vue";
import type { FormInstance } from "element-plus";
import { useRoute } from "vue-router";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

import ApiService from "@/core/services/ApiService";
import { hideModal } from "@/core/helpers/dom";

interface DescriptorItem {
  key: number;
  value: string;
}

interface MerchantDetail {
  merchant_type: string;
  mid_descriptor: string;
  mcc: string;
  logo_url: string;
  merchant_phone: string;
  dba_alias: string;
  merchant_url: string;
  merchant_address: string;
  merchant_email: string;
  descriptors: DescriptorItem[];
}

export default defineComponent({
  name: "edit-merchant-modal",
  components: {},
  props: {
    merchant: { type: Object, required: true },
  },
  emit: ["submit"],
  setup(props, { emit }) {
    const formRef = ref<FormInstance>();
    const dynamicValidateForm = ref<MerchantDetail>({
      descriptors: [
        {
          key: 1,
          value: "",
        },
      ],
      merchant_type: "Ecommerce",
      mid_descriptor: "",
      mcc: "",
      logo_url: "",
      merchant_phone: "",
      dba_alias: "",
      merchant_email: "",
      merchant_url: "",
      merchant_address: "",
    });
    const merchantTypes = ref(["Ecommerce", "Moto", "Retail", "Crypto"]);
    const route = useRoute();
    const getMerchantId = computed(() => {
      return route.params.id;
    });
    const loading = ref(false);
    const editMerchantModalRef = ref<HTMLElement | null>(null);

    watch(
      () => props.merchant,
      () => {
        dynamicValidateForm.value.merchant_type =
          props.merchant.merchantDetails.merchant_type;
        dynamicValidateForm.value.mid_descriptor =
          props.merchant.merchantDetails.mid_descriptor;
        dynamicValidateForm.value.mcc = props.merchant.merchantDetails.mcc;
        dynamicValidateForm.value.logo_url =
          props.merchant.merchantDetails.logo_url;
        dynamicValidateForm.value.merchant_phone =
          props.merchant.merchantDetails.merchant_phone;
        dynamicValidateForm.value.dba_alias =
          props.merchant.merchantDetails.dba_alias;
        dynamicValidateForm.value.merchant_url =
          props.merchant.merchantDetails.merchant_url;
        dynamicValidateForm.value.merchant_address =
          props.merchant.merchantDetails.merchant_address;
        dynamicValidateForm.value.merchant_email =
          props.merchant.merchantDetails.merchant_email;

        dynamicValidateForm.value.descriptors =
          props.merchant.descriptorVariations.map((ele) => ({
            key: ele,
            value: ele,
          }));
      }
    );

    const removeItem = (item: DescriptorItem) => {
      const index = dynamicValidateForm.value.descriptors.indexOf(item);
      if (index !== -1) {
        dynamicValidateForm.value.descriptors.splice(index, 1);
      }
    };

    const addItem = () => {
      dynamicValidateForm.value.descriptors.push({
        key: Date.now(),
        value: "",
      });
    };

    const submitForm = (formEl: FormInstance | undefined) => {
      if (!formEl) return;
      formEl.validate(async (valid) => {
        if (valid) {
          loading.value = true;
          try {
            const {
              merchant_type,
              mid_descriptor,
              mcc,
              logo_url,
              merchant_phone,
              dba_alias,
              merchant_url,
              merchant_address,
              merchant_email,
            } = dynamicValidateForm.value;
            const descriptorVariations =
              dynamicValidateForm.value.descriptors.map((ele) => ele.value);
            // eslint-disable-next-line
            const payload: any = {
              merchantDetails: {
                merchant_type,
                mid_descriptor,
                mcc,
                logo_url,
                merchant_phone,
                dba_alias,
                merchant_url,
                merchant_address,
                merchant_email,
              },
              descriptorVariations,
            };

            const res = await ApiService.put(
              `merchants/accounts/${getMerchantId.value}`,
              payload
            );
            if (res.data.success) {
              Swal.fire({
                text: "You have successfully updated the merchant detail!",
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: "Ok, got it!",
                customClass: {
                  confirmButton: "btn fw-bold btn-light-primary",
                },
              }).then(function () {
                hideModal(editMerchantModalRef.value);
                emit("submit");
              });
            } else {
              Swal.fire({
                text: res.data.error.message,
                icon: "error",
                buttonsStyling: false,
                confirmButtonText: "Try again!",
                customClass: {
                  confirmButton: "btn fw-bold btn-light-danger",
                },
              });
            }
          } catch (e) {
            Swal.fire({
              text: e.response.data.error
                ? e.response.data.error.message
                : "Something went wrong!",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Try again!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-danger",
              },
            });
          }
          loading.value = false;
        } else {
          return false;
        }
      });
    };

    const resetForm = (formEl: FormInstance | undefined) => {
      if (!formEl) return;
      formEl.resetFields();
    };

    const uploadLogo = async (evt) => {
      // eslint-disable-next-line
      const formData: any = new FormData();
      formData.append("upload", evt.target.files[0]);
      const res = await ApiService.post("imgupload", formData);
      if (res.data.success) {
        dynamicValidateForm.value.logo_url = res.data.data.url;
      }
    };

    return {
      formRef,
      dynamicValidateForm,
      removeItem,
      addItem,
      submitForm,
      resetForm,
      merchantTypes,
      loading,
      uploadLogo,
      editMerchantModalRef,
    };
  },
});
